import { ref, onValue, update, get } from 'firebase/database';
import { useEffect, useState } from 'react';
import { db } from '../firebase';

/**
 * 応募者履歴（イベント単位）。
 * events/{eventId}/applicant_stats/{identityKey} = { name, applied, won, last_won_at, won_prev }
 *
 * identityKey: アカウントありは user_id（uid）、なしは 'name:<vrchat_name小文字>'。
 * このノードは applications/guests/results のリセットでは消えない（兄弟ノード）。
 */

/** ゲスト記録から身元キーを求める */
export const guestIdentityKey = (guest) => {
  if (guest?.user_id) return guest.user_id;
  return 'name:' + (guest?.vrchat_name || '').trim().toLowerCase();
};

export const useApplicantStats = (eventId) => {
  const [rawStats, setRawStats] = useState({});
  const [stats, setStats] = useState({});

  useEffect(() => {
    setRawStats({});
    setStats({});
    if (!eventId) return;
    const statsRef = ref(db, `events/${eventId}/applicant_stats`);
    const unsub = onValue(statsRef, (snap) => setRawStats(snap.val() || {}));
    return () => unsub();
  }, [eventId]);

  // アカウント保持者の名前を users ノードの最新値で上書き
  useEffect(() => {
    const userIds = Object.keys(rawStats).filter(k => !k.startsWith('name:'));
    if (userIds.length === 0) {
      setStats(rawStats);
      return;
    }
    Promise.all(userIds.map(uid => get(ref(db, `users/${uid}/vrchat_name`)))).then(snaps => {
      const nameMap = {};
      userIds.forEach((uid, i) => {
        const name = snaps[i].val();
        if (name) nameMap[uid] = name;
      });
      const enriched = {};
      Object.entries(rawStats).forEach(([key, val]) => {
        enriched[key] = nameMap[key] ? { ...val, name: nameMap[key] } : val;
      });
      setStats(enriched);
    }).catch(() => {
      // 権限がない場合（staff等）はキャッシュ名をそのまま使用
      setStats(rawStats);
    });
  }, [rawStats]);

  /**
   * 1開催ぶんの最終状態を履歴に確定する（matched → waiting のコミット時に呼ぶ）。
   * @param {object} guests   events/{eventId}/guests の中身（最終状態）
   * @param {object} results  events/{eventId}/results（generated_at, pairs）
   */
  const commitOccurrence = async (guests, results) => {
    if (!eventId || !guests || Object.keys(guests).length === 0) return;

    const generatedAt = results?.generated_at || Date.now();
    const wonGuestIds = new Set(
      (results?.pairs || []).map((p) => p.guest_id).filter(Boolean)
    );

    // 既存の stats を読み込み（加算用）
    const snap = await get(ref(db, `events/${eventId}/applicant_stats`));
    const work = {};
    Object.entries(snap.val() || {}).forEach(([k, v]) => { work[k] = { ...v }; });

    // 「直近開催で当選したか」を正しく保つため、まず全員 won_prev=false に倒す
    Object.values(work).forEach((v) => { v.won_prev = false; });

    const deletions = {};

    Object.entries(guests).forEach(([guestId, guest]) => {
      const key = guestIdentityKey(guest);
      const cur = work[key] || { name: '', applied: 0, won: 0, last_won_at: null, won_prev: false };
      cur.name    = (guest.vrchat_name || cur.name || '').trim();
      cur.applied = (cur.applied || 0) + 1;
      if (wonGuestIds.has(guestId)) {
        cur.won = (cur.won || 0) + 1;
        cur.last_won_at = generatedAt;
        cur.won_prev = true;
      }

      // 遅延マージ: アカウント保持ゲストで、同名の name: キーが残っていれば畳み込む
      if (guest.user_id) {
        const nameKey = 'name:' + (guest.vrchat_name || '').trim().toLowerCase();
        if (nameKey !== key && work[nameKey]) {
          const old = work[nameKey];
          cur.applied     = (cur.applied || 0) + (old.applied || 0);
          cur.won         = (cur.won || 0) + (old.won || 0);
          cur.last_won_at = Math.max(cur.last_won_at || 0, old.last_won_at || 0) || null;
          // won_prev は今回の結果（上で設定済み）を優先
          delete work[nameKey];
          deletions[`events/${eventId}/applicant_stats/${nameKey}`] = null;
        }
      }

      work[key] = cur;
    });

    const updates = { ...deletions };
    Object.entries(work).forEach(([key, val]) => {
      updates[`events/${eventId}/applicant_stats/${key}`] = val;
    });
    await update(ref(db), updates);
  };

  return { stats, commitOccurrence };
};
